import { useRef, useState } from 'react';
import { compressToWebP } from '@/lib/imageUtils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  Loader2, Upload, Trash2, Download, FileText, FileSpreadsheet, FileImage, FolderOpen,
} from 'lucide-react';
import type { ApiError, SopDocument } from '@pms/shared';
import { sopService } from '@/services/sop.service';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';

const MAX_BYTES = 15 * 1024 * 1024;
const ALLOWED = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/png',
  'image/jpeg',
]);

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Icon, tint and short label for a document row, keyed off its MIME type. */
function fileKind(doc: SopDocument) {
  const mime = doc.mime_type;
  if (mime === 'application/pdf') {
    return { label: 'PDF', Icon: FileText, tint: 'bg-rose-500/10 text-rose-600 dark:text-rose-400' };
  }
  if (mime.includes('word')) {
    return { label: 'Word', Icon: FileText, tint: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' };
  }
  if (mime.includes('excel') || mime.includes('spreadsheet')) {
    return { label: 'Excel', Icon: FileSpreadsheet, tint: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' };
  }
  if (mime.startsWith('image/')) {
    return { label: 'Image', Icon: FileImage, tint: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' };
  }
  return { label: 'File', Icon: FileText, tint: 'bg-muted text-muted-foreground' };
}

export function DocumentsDialog({ sopId, sopTitle, onClose }: { sopId: string; sopTitle: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const docsQuery = useQuery({
    queryKey: ['sop', 'documents', sopId],
    queryFn: () => sopService.listDocuments(sopId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['sop', 'documents', sopId] });
    void queryClient.invalidateQueries({ queryKey: ['sop', 'sop', sopId] });
  };

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    if (!ALLOWED.has(file.type)) { toast.error('Allowed: PDF, Word, Excel, PNG, JPG'); return; }
    if (file.size > MAX_BYTES) { toast.error('File exceeds 15MB'); return; }
    const fileToUpload = await compressToWebP(file);
    setUploading(true);
    try {
      await sopService.uploadDocument(sopId, fileToUpload);
      toast.success('Document uploaded');
      invalidate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = useMutation({
    mutationFn: (docId: string) => sopService.deleteDocument(docId),
    onSuccess: () => { toast.success('Document deleted'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const download = async (docId: string, name: string) => {
    try {
      const blob = await sopService.downloadDocument(docId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const docs = docsQuery.data ?? [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* No auto-focus on open: it parked a heavy focus ring on the upload area.
          minmax(0,1fr): DialogContent is a grid, and its default `auto` column grows to a
          long file name's unwrapped width, pushing the rows out past the dialog edge. */}
      <DialogContent
        className="max-w-lg grid-cols-[minmax(0,1fr)]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="pr-6">
          <DialogTitle>Documents</DialogTitle>
          <DialogDescription className="truncate" title={sopTitle}>
            {sopTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); void onPick(e.dataTransfer.files?.[0]); }}
            className={cn(
              'flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
              dragOver
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/60 hover:bg-primary/5',
            )}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            </span>
            <span className="text-sm font-medium text-foreground">
              {uploading ? 'Uploading…' : 'Click to upload or drag a file here'}
            </span>
            <span className="text-xs text-muted-foreground">PDF, Word, Excel, PNG or JPG · up to 15 MB</span>
          </button>

          {docsQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading documents…
            </div>
          ) : docsQuery.isError ? (
            <div className="flex flex-col items-center gap-2 py-6 text-sm text-muted-foreground">
              {errMsg(docsQuery.error)}
              <Button size="sm" variant="outline" onClick={() => void docsQuery.refetch()}>Retry</Button>
            </div>
          ) : docs.length === 0 ? (
            <div className="flex flex-col items-center gap-1.5 py-6 text-center">
              <FolderOpen className="h-8 w-8 text-muted-foreground/60" />
              <p className="text-sm font-medium text-foreground">No documents yet</p>
              <p className="text-xs text-muted-foreground">Upload the files people need to read with this SOP.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Attached ({docs.length})
              </p>
              {docs.map((doc) => {
                const { label, Icon, tint } = fileKind(doc);
                const deleting = remove.isPending && remove.variables === doc.id;
                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/20 p-2.5 transition-colors hover:bg-muted/40"
                  >
                    <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-md', tint)}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <button
                      type="button"
                      onClick={() => download(doc.id, doc.original_name)}
                      title={doc.original_name}
                      className="min-w-0 flex-1 text-left"
                    >
                      <p className="truncate text-sm font-medium text-foreground hover:text-primary">{doc.original_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {label} · {formatSize(doc.size_bytes)} · {formatDate(doc.created_at)}
                      </p>
                    </button>
                    <div className="flex shrink-0 items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => download(doc.id, doc.original_name)}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        aria-label="Download"
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove.mutate(doc.id)}
                        disabled={remove.isPending}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                        aria-label="Delete"
                        title="Delete"
                      >
                        {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
